import React, { useEffect, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import useAmbientAudio, { AMBIENT_STORAGE_KEY } from '@/hooks/useAmbientAudio';

/**
 * AmbientControl — a small, classy floating toggle for ambient sound.
 *
 * Browser autoplay policy:
 *   Audio can never start without user gesture. This button IS that gesture.
 *   We render the control quietly in the corner; first click starts the fade-in.
 *
 * UX rules:
 *   - Visible only on the homepage to avoid noise on auth/admin/member portal screens.
 *   - Positioned at bottom-LEFT to not clash with WhatsApp / chat widgets.
 *   - Tiny pulse hint on first visit so users notice it (then hint goes away).
 *   - Preference saved in localStorage; auto-resumes on next visit after first click.
 */

const HINT_KEY = 'bitz-ambient-hint-seen';

const AmbientControl = () => {
  const { isPlaying, toggle } = useAmbientAudio();
  const [showHint, setShowHint] = useState(false);

  // Show the hint badge until the user interacts once
  useEffect(() => {
    try {
      const seen = localStorage.getItem(HINT_KEY);
      if (!seen) {
        const id = setTimeout(() => setShowHint(true), 2500);
        return () => clearTimeout(id);
      }
    } catch (_) {
      /* ignore */
    }
    return undefined;
  }, []);

  // BROTHER's request: continuous "star-hotel lobby" background music while browsing.
  // Browser autoplay policy requires a user gesture, so we listen for the FIRST
  // click/tap anywhere on the page and start the ambient pad automatically.
  // After that point the floating toggle still lets the user mute/unmute.
  useEffect(() => {
    let started = false;
    const tryStart = () => {
      if (started) return;
      started = true;
      try {
        const pref = localStorage.getItem(AMBIENT_STORAGE_KEY);
        // Auto-start unless the user has explicitly muted in a previous session
        if (pref !== '0' && !isPlaying) {
          toggle();
        }
      } catch (_) {
        if (!isPlaying) toggle();
      }
      setShowHint(false);
      try { localStorage.setItem(HINT_KEY, '1'); } catch (_) { /* ignore */ }
    };
    window.addEventListener('pointerdown', tryStart, { once: true });
    window.addEventListener('keydown', tryStart, { once: true });
    window.addEventListener('touchstart', tryStart, { once: true });
    return () => {
      window.removeEventListener('pointerdown', tryStart);
      window.removeEventListener('keydown', tryStart);
      window.removeEventListener('touchstart', tryStart);
    };
  }, [isPlaying, toggle]);

  const handleToggle = () => {
    setShowHint(false);
    try {
      localStorage.setItem(HINT_KEY, '1');
    } catch (_) {
      /* ignore */
    }
    toggle();
  };

  // If a previous session had it on, gentle invitation back on click (we cannot
  // autoplay without a gesture). We'll just let the user click again.
  useEffect(() => {
    try {
      // Read preference once for the hint copy
      const pref = localStorage.getItem(AMBIENT_STORAGE_KEY);
      if (pref === '1') setShowHint(true);
    } catch (_) {
      /* ignore */
    }
  }, []);

  return (
    <div
      className="fixed bottom-5 left-5 z-[60] flex items-center gap-3 pointer-events-none"
      data-testid="ambient-control"
    >
      {/* Hint pill — disappears after first interaction */}
      {showHint && !isPlaying && (
        <div
          className="hidden sm:flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#0F0F10]/85 backdrop-blur-md border border-[#D4AF37]/25 shadow-[0_18px_40px_-15px_rgba(0,0,0,0.6)] pointer-events-none animate-[fadeIn_0.6s_ease-out]"
          style={{ animationDelay: '300ms' }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
          <span
            className="text-[10px] tracking-[0.3em] uppercase text-gray-200"
            style={{ fontFamily: 'Cinzel, serif' }}
          >
            Lobby music · Tap anywhere
          </span>
        </div>
      )}

      <button
        type="button"
        onClick={handleToggle}
        aria-label={isPlaying ? 'Mute ambient sound' : 'Enable ambient sound'}
        aria-pressed={isPlaying}
        data-testid="ambient-toggle-btn"
        className={`pointer-events-auto group relative w-11 h-11 sm:w-12 sm:h-12 rounded-full backdrop-blur-md border transition-all duration-500 flex items-center justify-center shadow-[0_18px_40px_-12px_rgba(0,0,0,0.55)] ${
          isPlaying
            ? 'bg-[#D4AF37]/15 border-[#D4AF37]/60 text-[#D4AF37]'
            : 'bg-[#0F0F10]/80 border-white/15 text-gray-200 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]'
        }`}
      >
        {/* Subtle rotating gold ring when playing */}
        {isPlaying && (
          <span
            className="absolute inset-0 rounded-full border border-[#D4AF37]/30 animate-[ping_3.2s_ease-out_infinite]"
            aria-hidden
          />
        )}
        {isPlaying ? (
          <Volume2 className="w-5 h-5 sm:w-5 sm:h-5" />
        ) : (
          <VolumeX className="w-5 h-5 sm:w-5 sm:h-5" />
        )}
      </button>
    </div>
  );
};

export default AmbientControl;
